import React, { useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { salesforceTableHeadSx, toggleExpandedRow } from '../surcharges/surchargeTabLayout';
import type { RoomRateSelection } from '../surcharges/surchargeUtils';

import { type EgsApplyCandidate, canApplyEgsCandidate } from './egsApplyCandidates';
import { formatSlotList } from './egsUtils';

const COLUMN_COUNT = 5;

interface Props {
  candidates: Array<EgsApplyCandidate>;
  selectedRoomRateIdsByRoomLabel: Record<string, Array<string>>;
  toggleRoomRate: (roomLabel: string, roomRateId: string, checked: boolean) => void;
}

/** Safe as a loop because the setter takes a functional update, so each call sees the previous selection. */
function setAllSelected(
  roomLabel: string,
  selections: Array<RoomRateSelection>,
  checked: boolean,
  toggleRoomRate: Props['toggleRoomRate'],
) {
  selections.forEach((selection) => toggleRoomRate(roomLabel, String(selection.roomRate.id), checked));
}

/** `Pick rooms` is the only state that is not yet an answer, so it is the only one worn as a warning. */
function RoomsCell({
  canApply,
  offeredCount,
  requiresPick,
  selectedCount,
}: {
  canApply: boolean;
  offeredCount: number;
  requiresPick: boolean;
  selectedCount: number;
}) {
  // A verdict that narrowed to no room at all, which an unreadable name no longer does.
  if (!canApply) {
    return <Chip label="No rooms" size="small" variant="outlined" />;
  }

  if (selectedCount === 0) {
    return requiresPick ? (
      <Chip color="warning" label="Pick rooms" size="small" variant="outlined" />
    ) : (
      <Chip label="Skipped" size="small" variant="outlined" />
    );
  }

  if (selectedCount === offeredCount) {
    return <Chip color="success" label={`All rooms (${offeredCount})`} size="small" variant="outlined" />;
  }

  return <Chip color="info" label={`${selectedCount} of ${offeredCount} rooms`} size="small" variant="outlined" />;
}

function SurchargeRow({
  candidate,
  isExpanded,
  onToggleExpanded,
  selectedIds,
  toggleRoomRate,
}: {
  candidate: EgsApplyCandidate;
  isExpanded: boolean;
  onToggleExpanded: (roomLabel: string) => void;
  selectedIds: Set<string>;
  toggleRoomRate: Props['toggleRoomRate'];
}) {
  const { candidates: selections, roomLabel } = candidate;
  const canApply = canApplyEgsCandidate(candidate);
  const selectedCount = selections.filter((selection) => selectedIds.has(String(selection.roomRate.id))).length;

  // All the rooms offered or none of them; anything narrower is the Edit list below.
  const handleToggleAll = (checked: boolean) => setAllSelected(roomLabel, selections, checked, toggleRoomRate);

  return (
    <>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            checked={selectedCount > 0}
            disabled={!canApply}
            indeterminate={selectedCount > 0 && selectedCount < selections.length}
            inputProps={{ 'aria-label': roomLabel }}
            onChange={(event) => handleToggleAll(event.target.checked)}
            size="small"
          />
        </TableCell>
        <TableCell>
          {/* The full Salesforce name, unedited: an exclusion clause is the only place a room is ruled out. */}
          <Stack alignItems="center" direction="row" spacing={1}>
            <Typography variant="body2">{roomLabel}</Typography>
            {candidate.ratePlanQualifier && (
              <Chip color="warning" label={candidate.ratePlanQualifier} size="small" variant="outlined" />
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Typography color="text.secondary" variant="body2">
            {formatSlotList(candidate.slots)}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <RoomsCell
            canApply={canApply}
            offeredCount={selections.length}
            requiresPick={candidate.requiresPick}
            selectedCount={selectedCount}
          />
        </TableCell>
        <TableCell align="right">
          {canApply && (
            <Button
              endIcon={<ExpandMoreIcon sx={{ transform: isExpanded ? 'rotate(180deg)' : undefined }} />}
              onClick={() => onToggleExpanded(roomLabel)}
              size="small"
              variant="text"
            >
              Edit
            </Button>
          )}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={COLUMN_COUNT} sx={{ border: 0, p: 0 }}>
          <Collapse in={isExpanded} unmountOnExit>
            <Stack spacing={0.5} sx={{ pb: 2, pl: 6, pr: 2 }}>
              <Stack alignItems="center" direction="row" justifyContent="space-between">
                <Typography color="text.secondary" variant="caption">
                  {candidate.ratePlanQualifier
                    ? `Salesforce priced this for ${candidate.ratePlanQualifier} only. Tick the rooms it should apply to, or leave it out if that is not the plan you picked.`
                    : 'Tick the rooms this surcharge should apply to.'}
                </Typography>
                {selections.length > 1 && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      disabled={selectedCount === selections.length}
                      onClick={() => handleToggleAll(true)}
                      size="small"
                      variant="text"
                    >
                      Select all
                    </Button>
                    <Button
                      disabled={selectedCount === 0}
                      onClick={() => handleToggleAll(false)}
                      size="small"
                      variant="text"
                    >
                      Clear
                    </Button>
                  </Stack>
                )}
              </Stack>
              {selections.map((selection) => {
                const roomRateId = String(selection.roomRate.id);

                return (
                  <FormControlLabel
                    key={roomRateId}
                    control={
                      <Checkbox
                        checked={selectedIds.has(roomRateId)}
                        onChange={(event) => toggleRoomRate(roomLabel, roomRateId, event.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">{selection.roomType.name}</Typography>}
                  />
                );
              })}
            </Stack>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// Rows the matcher resolved arrive ticked, so this is a confirmation except where a name did not resolve.
export default function EgsSurchargeMappingStep({ candidates, selectedRoomRateIdsByRoomLabel, toggleRoomRate }: Props) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  // Only surcharges the CM could have ticked, so a row offering no room is not counted as a choice.
  const skippedCount = candidates.filter(
    (candidate) =>
      canApplyEgsCandidate(candidate) && (selectedRoomRateIdsByRoomLabel[candidate.roomLabel] ?? []).length === 0,
  ).length;

  const toggleExpanded = (roomLabel: string) => setExpandedRows((current) => toggleExpandedRow(current, roomLabel));

  return (
    <Stack spacing={1}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            {/* Blue for Salesforce data, as on the blackout, surcharges and campaign schedule tabs. */}
            <TableRow sx={salesforceTableHeadSx}>
              <TableCell padding="checkbox" />
              <TableCell>Salesforce surcharge</TableCell>
              <TableCell>Guest types</TableCell>
              <TableCell align="center">Rooms</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {candidates.map((candidate) => (
              <SurchargeRow
                key={candidate.roomLabel}
                candidate={candidate}
                isExpanded={expandedRows.has(candidate.roomLabel)}
                onToggleExpanded={toggleExpanded}
                selectedIds={new Set(selectedRoomRateIdsByRoomLabel[candidate.roomLabel] ?? [])}
                toggleRoomRate={toggleRoomRate}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {skippedCount > 0 && (
        <Alert severity="info">
          {/* Not "for this offer": a name the matcher could not read is still one of this offer's own rows. */}
          {`${skippedCount} ${skippedCount === 1 ? 'surcharge has' : 'surcharges have'} no room ticked, so ${skippedCount === 1 ? 'it' : 'they'} will be skipped. Leave ${skippedCount === 1 ? 'it' : 'them'} out if ${skippedCount === 1 ? 'it is' : 'they are'} not for this rate plan.`}
        </Alert>
      )}
    </Stack>
  );
}
